import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from "react-router-dom";
import { LinkButton, Button } from 'components/buttons'
import { ListingTable } from "components/listing";
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { LicenceCheckingService } from 'services';
import { NotificationToaster, AlertConfirm } from 'components/notifications';
import { Intent } from '@blueprintjs/core';
import { Numeric } from "components/formatting";
import moment from "moment";

export function LicenceCheckingPolicies() {

    const defaultRecordSize = 25;
    const dispatch = useDispatch();

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(true);
    const [selectedPolicyId, setSelectedPolicyId] = useState(0);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const noDataMessage = "You have no policies, please click 'Add new policy' to get started.";
    const tableHeaders = ["Policy", "Status", "Start Date", "Base Check Frequency", "Employee Count", "Estimated Annual Cost", "Actions"];
    const sortableHeaders = [{ headerName: "Estimated Annual Cost", sortName: "estimatedannualcreditcost" }, { headerName: "Policy", sortName: "policyname" }, { headerName: "Employee Count", sortName: "employeecount" }, { headerName: "Start Date", sortName: "startdate" }];
    const [tableSortBy, setTableSortBy] = useState(sortableHeaders[0]);
    const [tableSortDir, setTableSortDir] = useState("D");
    const requiredAction = "LicenceChecking:Manage";
    const canAccessPage = UserHasAccess(requiredAction);

    useEffect(() => {
        dispatch({ type: 'SITE_FULL_WIDTH' });
    }, []);

    useEffect(() => {
        if (loading && canAccessPage) {
            LicenceCheckingService.searchPolicies(pageSize, pageNumber, tableSortBy.sortName, tableSortDir).then(r => {
                let data = [];

                if (r?.data != null) {
                    data = r.data.map((p) => {
                        return [(<Link key={`link-to-policy-${p.policyId}`} to={`/licence-checking/policies/${p.policyId}`}>{p.policyName}</Link>),
                            p.status, moment(p.startDate).format('DD/MM/YYYY'), p.baseCheckFrequency,
                            <><Numeric>{p.employeeCount}</Numeric><span> (</span><Numeric>{p.enrolledEmployeeCount}</Numeric><span> currently enrolled)</span></>,
                            <><Numeric>{p.estimatedAnnualCreditCost}</Numeric><span> (</span><Numeric>{p.enrolledEstimatedAnnualCreditCost}</Numeric><span> based on enrollment)</span></>,
                        <div key={`delete-policy-${p.policyId}`} className="button-row-small">
                            <Button onClick={() => deletePolicy(p.policyId)} icon="trash" iconOnly={true} text="Delete Integration" large={false} minimal={true}></Button>
                        </div>];
                    })

                    setTableRows(data);
                    setTotalRecords(r.totalCount);
                }
            }).catch(() => {
                NotificationToaster.show(Intent.DANGER, "Failed to get policy listing. Please refresh the page.");
            }).finally(() => {
                setLoading(false);
                setShowDeleteConfirm(false);
                setDeleting(false);
            });
        }
    }, [loading, pageSize, pageNumber, tableSortBy, tableSortDir]);

    function deletePolicy(id) {
        setSelectedPolicyId(id);
        setShowDeleteConfirm(true);
    }

    function onDeleteConfirm() {
        setDeleting(true);

        LicenceCheckingService.deletePolicy(selectedPolicyId).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "Policy has been successfully deleted.");
            setLoading(true);
            setSelectedPolicyId(0);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, "Failed to delete policy listing. Please try again.");
            setShowDeleteConfirm(false);
            setDeleting(false);
        });
    }

    function onDeleteCancel() {
        setSelectedPolicyId(0);
        setShowDeleteConfirm(false);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
        setLoading(true);
    }

    function onTableSort(header, direction) {
        setTableSortBy(header);
        setTableSortDir(direction);
        setLoading(true);
    }

    return (

        <UserAccess perform={requiredAction}
            yes={() => (
                <>
                    <div>
                        <div className="pull-left spacer-bottom">
                            <LinkButton intent="primary" text="New Policy" href="/licence-checking/policies/create" id="new-policy" />
                        </div>

                        <ListingTable
                            id="policy-listing"
                            headers={tableHeaders}
                            data={tableRows}
                            totalRecordCount={totalRecords}
                            onPagingChange={onPagingChange}
                            loadingData={loading}
                            noDataMessage={noDataMessage}
                            sortable
                            sortableHeaders={sortableHeaders}
                            sortedBy={tableSortBy}
                            sortedDir={tableSortDir}
                            onSort={onTableSort}
                        />
                    </div>

                    <AlertConfirm
                        title="Please confirm"
                        isOpen={showDeleteConfirm}
                        onConfirm={onDeleteConfirm}
                        onCancel={onDeleteCancel}
                        saving={deleting}
                    >
                        <p>Are you sure you want to delete this policy?</p>
                    </AlertConfirm>
                </>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}